import React, { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CollapsibleNe from "./Collapsible";
import AddPortService from "../../services/addPort.service";
import ManagePortsService from "../../services/managePorts.service";
import { AuthRoutes, SortOrder } from "../../enums";
import GroupOperation from "./GroupOperations";
import SuccessInfoIcon from "../../assets/images/success-toast-checkmark-icon.svg";
import { SuccessMessage, ErrorCodeMessageMapping, ErrorCodesMapping, ErrorMessages as Errors } from "../../utils/apiConstants";
import ErrorInfoIcon from "../../assets/images/error-info-icon.svg";
import EditPortDrawer from "./Table/EditPortDetails";
import { TableConstants } from "../../utils/appConstants";
import { sumCount, throwGenericToasterError } from "../../utils/methods";
import Button from "../DesignSystem/button";
import ListPortsTable from "./Table/ListPortTable";
import Input from "../DesignSystem/input";
import SearchIcon from "../../assets/images/search-icon.svg";
import NodeView from "../../assets/images/node-view.svg";
import NodeViewSelected from "../../assets/images/node-view-selected.svg";
import ListView from "../../assets/images/list-icon.svg";
import ListViewSelected from "../../assets/images/list-icon-selected.svg";
import { useForSalePortHook } from "./Table/PutUpForSale/useForSalePortHook";
import { useIsMounted } from "../../utils/customHooks";
import Dropdown from "../DesignSystem/dropdown_autocomplete";
import { useGroup } from "./GroupOperations/useGroup";

const ManagePorts: React.FC = () => {
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { state: networkElementContext } = useLocation() as any;
  const [isNodeView, setIsNodeView] = useState<boolean>(!!networkElementContext);
  const [listPorts, setListPorts] = useState<any>("");
  const [listLoading, setListLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>("");
  const [prevSearchText, setPrevSearchText] = useState<string>("");
  const [networkElements, setNetworkElements] = useState<any[]>([]);
  const [groupedSelected, setGroupSelected] = useState<any[]>([]);
  const [unGroupedSelected, setUnGroupSelected] = useState<any[]>([]);
  const [selectedUnGroupId, setSelectedUnGroupId] = useState<string>("");
  const [order, setOrder] = useState<SortOrder>(SortOrder.asc);
  const [orderBy, setOrderBy] = useState<string>("port_description");
  const [expandedNeId, setExpandedNeId] = useState<string>("");
  const [portsGroupsLoading, setPortsGroupsLoading] = useState<boolean>(false);

  const [portsInNe, setPortsInNe] = useState<any[]>([]);
  const [groupsInNe, setGroupsInNe] = useState<any[]>([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState<string[]>([]);
  const [expandedOnNet, setExpandedOnNet] = useState<boolean>(false);
  const [neInput, setNeInput] = useState<string>("");
  const handleNeInputChange = useCallback(
    (input: string) => {
      setNeInput(input);
    },
    [setNeInput]
  );
  const [unGroupLoading, setUnGroupLoading] = useState<boolean>(false);

  const [searchTextNeElements, setSearchTextNeElements] = useState<string>(networkElementContext?.ne_name || "");
  const [groupLoading, setGroupLoading] = useState<boolean>(false);

  const handleNeValueChange = useCallback(
    (value: string) => {
      setSearchTextNeElements(value);
    },
    [setSearchTextNeElements]
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [cancelSubscriptionLoading, setCancelSubscriptionLoading] = useState<string>("");
  const [editPortOpen, setEditPortOpen] = useState<boolean>(false);
  const [selectedRowForEdit, setSelectedRowForEdit] = useState<any>();
  const [portNameToEdit, setPortNameToEdit] = useState<string>("");
  const [portDescriptionToEdit, setPortDescriptionToEdit] = useState<string>("");

  const handleCollapsibleGroupOpen = useCallback(
    (groupId: string) => {
      setExpandedGroupIds(Array.from(new Set([...expandedGroupIds, groupId])));
    },
    [setExpandedGroupIds, expandedGroupIds]
  );

  const handleCollapsibleGroupClose = useCallback(
    (groupId: string) => {
      const newIds = expandedGroupIds.filter((item) => item !== groupId);
      setExpandedGroupIds(Array.from(new Set([...newIds])));
    },
    [setExpandedGroupIds, expandedGroupIds]
  );

  const [seeMore, setSeeMore] = useState(true);

  const fetchNetworkElements = useCallback(
    (seeMore = false) => {
      AddPortService.getNetworkElements()
        .then((response: any) => {
          if (!isMounted) return;
          /*
          Displays all the network elements on which you have resources.
          If you have less than minNEs network resources, show all the resources
          Sort them by the number of resources and secondly by if they are on net or not
        */
          const minNEs = 8;
          const populatedNEs = response.data.filter((ne: any) => sumCount(ne.nrCount) > 0);
          // TODO:|| (networkElementContext && ne.id === networkElementContext?.id));
          const NEs = response.data && populatedNEs && populatedNEs.length >= minNEs && !seeMore ? populatedNEs : response.data || [];
          setSeeMore(seeMore || !(response.data && populatedNEs && populatedNEs.length >= minNEs && !seeMore));
          NEs.sort((a: any, b: any) => b.on_net - a.on_net);
          NEs.sort((a: any, b: any) => sumCount(b.nrCount) - sumCount(a.nrCount));
          // if (neIdFromNavigate) {
          //   NEs.sort((a: any, b: any) => b.id === neIdFromNavigate);
          // }
          if (searchTextNeElements) {
            NEs.sort((a: any, b: any) => b.ne_name === searchTextNeElements);
          }
          setNetworkElements(NEs);
        })
        .catch(() => {
          if (!isMounted) return;
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
          setNetworkElements([]);
        });
    },
    [setNetworkElements, seeMore, searchTextNeElements]
  );

  const fetchPortsAndGroups = useCallback(() => {
    if (expandedNeId) {
      setPortsGroupsLoading(true);
      ManagePortsService.getPortsAndGroups(expandedNeId, searchText, orderBy, order.toUpperCase())
        .then((response: any) => {
          if (!isMounted) return;
          if (!response.data[0]) {
            throw Errors.typeScriptError;
          }
          setPortsInNe(response.data[0].ports || []);
          setGroupsInNe(response.data[0].groups || []);
          setExpandedGroupIds(response.data[0].groups && response.data[0].groups.map((group: any) => group.groupId));
          setPortsGroupsLoading(false);
        })
        .catch((e) => {
          if (!isMounted) return;
          if (e !== Errors.typeScriptError) {
            toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
          }
          setPortsGroupsLoading(false);
          setPortsInNe([]);
          setGroupsInNe([]);
        });
    }
  }, [expandedNeId, searchText, order, orderBy, setPortsGroupsLoading, setPortsInNe, setGroupsInNe]);

  const fetchListPorts = useCallback(() => {
    setListLoading(true);
    const payload = {
      sortField: orderBy,
      sortDirection: order.toUpperCase(),
      page,
      pageSize: TableConstants.perPage,
      searchText,
      networkElement: searchTextNeElements,
    };
    ManagePortsService.getManagePortsList(payload)
      .then((response: any) => {
        if (!isMounted) return;
        setListPorts(response.data || []);
        setPrevSearchText(searchText);
        setTotal(response?.pagination?.totalRecords ?? 0);
      })
      .catch(() => {
        if (!isMounted) return;
        setListPorts([]);
        setTotal(0);
      });
    setListLoading(false);
  }, [loading, setListLoading, setTotal, searchTextNeElements, setListPorts, searchText, order, orderBy, page, setPage]);
  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  useEffect(() => {
    fetchPortsAndGroups();
  }, [expandedNeId]);

  useEffect(() => {
    fetchNetworkElements(seeMore);
    fetchListPorts();
  }, [page, order, orderBy, searchTextNeElements]);

  useEffect(() => {
    refetch();
    window.history.replaceState({}, document.title);
  }, [isNodeView]);

  const handleKeypress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        fetchListPorts();
      }
    },
    [fetchListPorts]
  );
  const handleSearchChange = useCallback(
    (event) => {
      setSearchText(event.target.value);
      if (!event.target.value) {
        fetchListPorts();
        setSearchText("");
      }
    },
    [setSearchText]
  );

  const handleCollapsibleNEOpen = useCallback(
    (neId: string) => {
      setExpandedNeId(neId);
      networkElements.forEach((ne: any) => {
        if (Number(ne.id) === Number(neId)) {
          setExpandedOnNet(ne.on_net);
        }
      });
      setGroupSelected([]);
      setUnGroupSelected([]);
      setSelectedUnGroupId("");
      setOrder(SortOrder.asc);
      setOrderBy("port_description");
      setExpandedGroupIds([]);
    },
    [setExpandedOnNet, networkElements, setExpandedNeId, setGroupSelected, setUnGroupSelected, setSelectedUnGroupId, setOrderBy, setOrder, setExpandedGroupIds]
  );

  const handleCollapsibleNEClose = useCallback(() => {
    setExpandedNeId("");
    setExpandedOnNet(false);
  }, [setExpandedNeId, setExpandedOnNet]);

  const handleRequestSort = useCallback(
    (property: string, nextSortOrder: SortOrder) => {
      setOrder(nextSortOrder);
      setOrderBy(property);
    },
    [orderBy, order]
  );

  const handleRowSelect = useCallback(
    (newSelected: any[]) => {
      setGroupSelected(newSelected);
      // set ports that are not verified
      if (expandedOnNet) {
        let reqVar = true;
        portsInNe.forEach((portInNe: any) => {
          newSelected.forEach((portSelected: any) => {
            if (
              (parseInt(portInNe.nr_id) === parseInt(portSelected.nrId) && parseInt(portInNe.requested_verification) === 1) ||
              parseInt(portSelected.nrId) === -1
            ) {
              reqVar = false;
            }
          });
        });
      }
    },
    [setGroupSelected, portsInNe, expandedOnNet]
  );

  const handleGroupRowSelect = useCallback(
    (newSelected: any[], groupId: string) => {
      setSelectedUnGroupId(groupId);
      setUnGroupSelected(newSelected);
      if (expandedOnNet) {
        let reqVar = true;
        groupsInNe.forEach((groups: any) => {
          groups.ports.forEach((portInNe: any) => {
            newSelected.forEach((portSelected: any) => {
              if (
                (parseInt(portInNe.nr_id) === parseInt(portSelected.nrId) && parseInt(portInNe.requested_verification) === 1) ||
                parseInt(portSelected.nrId) === -1
              ) {
                reqVar = false;
              }
            });
          });
        });
      }
    },
    [setUnGroupSelected, setSelectedUnGroupId, groupsInNe, expandedOnNet]
  );

  const handleRequestVerificationClick = useCallback(
    (port: any) => {
      const payload = {
        nrIds: [port.nr_id],
      };
      ManagePortsService.setRequestVerification(payload)
        .then((response: any) => {
          if (!isMounted) return;
          toast.success(SuccessMessage.requestVerificationSuccessful, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
          setGroupSelected([]);
          setUnGroupSelected([]);
          setSelectedUnGroupId("");
          if (isNodeView) {
            fetchPortsAndGroups();
          } else {
            fetchListPorts();
          }
        })
        .catch((err) => {
          if (!isMounted) return;
          throwGenericToasterError(err);
        });
    },
    [fetchPortsAndGroups, isNodeView, fetchListPorts, setSelectedUnGroupId, setGroupSelected, setUnGroupSelected, setUnGroupLoading, setGroupLoading]
  );
  const handleUnGroupClick = useCallback(() => {
    setUnGroupLoading(true);

    const payload = {
      groupId: selectedUnGroupId,
      nrIds: unGroupedSelected.filter((item) => item.nrId !== -1).map((item) => item.nrId),
      purchasePortIds: unGroupedSelected.filter((item) => item.purchasePortId !== -1).map((item) => item.purchasePortId),
    };
    ManagePortsService.unGroupNrs(payload)
      .then((response: any) => {
        if (!isMounted) return;
        toast.success(SuccessMessage.unGroupedSuccess, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
        setUnGroupLoading(false);
        setGroupSelected([]);
        setUnGroupSelected([]);
        setSelectedUnGroupId("");
        fetchPortsAndGroups();
      })
      .catch(() => {
        if (!isMounted) return;
        toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        setUnGroupLoading(false);
      });
  }, [setUnGroupLoading, fetchPortsAndGroups, setGroupSelected, setUnGroupSelected, setSelectedUnGroupId, selectedUnGroupId, unGroupedSelected]);

  const [editPortError, setEditPortError] = useState<string>("");
  const [editLoading, setEditLoading] = useState<boolean>(false);

  const handleDeleteOffNetPort = useCallback(() => {
    const payload = {
      portId: selectedRowForEdit.nr_id || 0,
    };

    ManagePortsService.deleteOffNetPort(payload)
      .then((response: any) => {
        if (!isMounted) return;
        if (isNodeView) {
          fetchPortsAndGroups();
        } else {
          fetchListPorts();
        }

        setPortNameToEdit("");
        setPortDescriptionToEdit("");
        setSelectedRowForEdit("");
        setEditPortError("");
        setEditPortOpen(false);

        setTimeout(() => {
          if (!isMounted) return;
          toast.success(SuccessMessage.deletePort, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
        });
      })
      .catch((e: any) => {
        if (!isMounted) return;
        toast.error("Delete Port Error", { icon: <img src={ErrorInfoIcon} alt="Error" /> });
      });
    setEditLoading(false);
  }, [selectedRowForEdit, isNodeView, fetchPortsAndGroups, fetchListPorts]);

  const handleEditPortClick = useCallback(
    (port: any) => {
      setPortNameToEdit(port.port_name || "");
      setPortDescriptionToEdit(port.port_description || "");
      setSelectedRowForEdit(port);
      setEditPortError("");
      setEditPortOpen(true);
    },
    [setPortNameToEdit, setEditPortError, setPortDescriptionToEdit, setEditPortOpen, setSelectedRowForEdit]
  );

  const handleCloseEditPort = useCallback(() => {
    setEditPortOpen(false);
    setPortNameToEdit("");
    setPortDescriptionToEdit("");
    setSelectedRowForEdit("");
    setEditPortError("");
  }, [setEditPortOpen, setEditPortError, setPortNameToEdit, setPortDescriptionToEdit, setSelectedRowForEdit]);
  const handleCancelSubscription = useCallback(
    (port: any) => {
      setCancelSubscriptionLoading(port.id);
      const payload: any = {
        pportId: port.pportId,
        id: port.id,
      };

      ManagePortsService.cancelSubscription(payload)
        .then((response: any) => {
          if (!isMounted) return;
          toast.success(SuccessMessage.cancelSubscription, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
          if (isNodeView) {
            fetchNetworkElements();
            fetchPortsAndGroups();
          } else {
            fetchListPorts();
          }
          setCancelSubscriptionLoading("");
          handleCloseEditPort();
        })
        .catch((e: any) => {
          if (!isMounted) return;
          setCancelSubscriptionLoading("");
          if (e.errorCode === ErrorCodesMapping[1012]) {
            toast.error(ErrorCodeMessageMapping[1012], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
            if (isNodeView) {
              fetchNetworkElements();
              fetchPortsAndGroups();
            } else {
              fetchListPorts();
            }
          } else {
            toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
          }
        });
    },
    [setCancelSubscriptionLoading, fetchPortsAndGroups, fetchNetworkElements, isNodeView, fetchListPorts]
  );
  const handlePortNameChange = useCallback(
    (e: any) => {
      setPortNameToEdit(e.target.value);
      setEditPortError("");
    },
    [setPortNameToEdit, setEditPortError]
  );

  const handlePortDescriptionChange = useCallback(
    (e: any) => {
      setPortDescriptionToEdit(e.target.value);
      setEditPortError("");
    },
    [setPortDescriptionToEdit, setEditPortError]
  );

  const handlePortViewChange = useCallback(
    (checked: boolean) => {
      setIsNodeView(checked);
      setOrderBy("port_description");
      setPage(1);
      setTotal(0);
    },
    [setIsNodeView, setOrderBy, setPage, setTotal]
  );

  const refetch = useCallback(() => {
    fetchNetworkElements();
    if (isNodeView) {
      fetchPortsAndGroups();
      setExpandedNeId("");
    } else {
      fetchListPorts();
      setPage(1);
    }
  }, [isNodeView]);

  // expandedNeId,
  // groupedSelected,
  // { isBandwidth: false, allowAddToExisting: true },
  // isMounted,
  // refetch
  const { GroupDrawerProps } = useGroup(
    expandedNeId,
    { isMounted, isBandwidth: false, lowerDrawer: false },
    { groupedSelected, allowAddToExisting: true, refetch }
  );
  const { SaleDrawerComponent, handlePutupForSaleClick, handleSaleEdit, handlePutForSaleOpen, removeLoadingId, handleRemoveClick } = useForSalePortHook(
    isMounted,
    refetch,
    loading,
    setLoading
  );
  // const {} = useManageLeasedPorts(
  //   selecte
  // )

  const [collapsibleContent, setCollapsibleContent] = useState<any>();
  const updateCollapsibleContent = useCallback(() => {
    const CC = networkElements.map((ne: any, index: number) => {
      if (Number(expandedNeId) === Number(ne.id)) {
        return (
          <CollapsibleNe
            onRequestVerification={handleRequestVerificationClick}
            groupLoading={groupLoading}
            handleGroupClick={GroupDrawerProps.addGroup}
            unGroupLoading={unGroupLoading}
            handleUnGroupClick={handleUnGroupClick}
            ne={ne}
            on_net={ne.on_net}
            onEditPort={handleEditPortClick}
            onCancelSubscription={handleCancelSubscription}
            onSaleClick={handlePutForSaleOpen}
            onSaleEdit={handleSaleEdit}
            onRemoveClick={handleRemoveClick}
            cancelSubLoading={cancelSubscriptionLoading}
            removeLoadingId={removeLoadingId}
            key={index}
            open={Number(expandedNeId) === Number(ne.id)}
            ports={portsInNe}
            groups={groupsInNe}
            loading={portsGroupsLoading}
            selectedUnGroupId={selectedUnGroupId}
            order={order}
            orderBy={orderBy}
            selected={groupedSelected}
            unGroupedSelected={unGroupedSelected}
            id={ne.id}
            text={ne.ne_name}
            expandedGroupIds={expandedGroupIds}
            onCollapsibleNEClose={handleCollapsibleNEClose}
            onCollapsibleNEOpen={handleCollapsibleNEOpen}
            onRequestSort={handleRequestSort}
            onSelectRow={handleRowSelect}
            onGroupRowSelect={handleGroupRowSelect}
            onCollapsibleGroupOpen={handleCollapsibleGroupOpen}
            onCollapsibleGroupClose={handleCollapsibleGroupClose}
            onEditGroupClick={GroupDrawerProps.editGroup}
            refetch={refetch}
          />
        );
      }
      return (
        <CollapsibleNe
          onRequestVerification={() => {}}
          groupLoading={groupLoading}
          handleGroupClick={GroupDrawerProps.addGroup}
          unGroupLoading={unGroupLoading}
          handleUnGroupClick={handleUnGroupClick}
          ne={ne}
          open={false}
          on_net={ne.on_net}
          onEditPort={() => {}}
          onCancelSubscription={() => {}}
          onSaleClick={() => {}}
          onSaleEdit={() => {}}
          onRemoveClick={() => {}}
          cancelSubLoading=""
          removeLoadingId=""
          key={index}
          ports={[]}
          groups={[]}
          loading={false}
          selectedUnGroupId=""
          order={order}
          orderBy=""
          selected={[]}
          unGroupedSelected={[]}
          id={ne.id}
          text={ne.ne_name}
          expandedGroupIds={[]}
          onCollapsibleNEClose={handleCollapsibleNEClose}
          onCollapsibleNEOpen={handleCollapsibleNEOpen}
          onRequestSort={() => {}}
          onSelectRow={() => {}}
          onGroupRowSelect={() => {}}
          onCollapsibleGroupOpen={() => {}}
          onCollapsibleGroupClose={() => {}}
          onEditGroupClick={() => {}}
          refetch={refetch}
        />
      );
    });
    setCollapsibleContent([
      ...CC,
      <Button
        className="see-more-button"
        key={networkElements.length + 1 || 0}
        type="reset"
        variant="greyed"
        node={seeMore ? "See Less" : "See More"}
        onClick={() => {
          fetchNetworkElements(!seeMore);
        }}
      />,
    ]);
  }, [
    fetchNetworkElements,
    handleRequestVerificationClick,
    setNetworkElements,
    removeLoadingId,
    expandedNeId,
    portsInNe,
    groupsInNe,
    portsGroupsLoading,
    selectedUnGroupId,
    order,
    orderBy,
    groupedSelected,
    unGroupedSelected,
    expandedGroupIds,
    handleCollapsibleNEClose,
    handleCollapsibleNEOpen,
    handleRequestSort,
    handleRowSelect,
    handleGroupRowSelect,
    handleCollapsibleGroupOpen,
    handleCollapsibleGroupClose,
    networkElements,
    expandedNeId,
    handleEditPortClick,
    handleCancelSubscription,
    handlePutForSaleOpen,
    handleSaleEdit,
    handleSaleEdit,
    cancelSubscriptionLoading,
  ]);

  const currentNetworkElement = useMemo(() => {
    if (isNodeView && expandedNeId) {
      return networkElements.find((ele: any) => ele.id === expandedNeId);
    }
    return networkElements.find((ele: any) => ele.ne_name === searchTextNeElements);
  }, [isNodeView, networkElements, expandedNeId, searchTextNeElements]);

  useEffect(() => {
    if (networkElementContext && networkElementContext.id) {
      handleCollapsibleNEOpen(networkElementContext.id);
      window.history.replaceState({}, document.title);
    }
  }, [networkElements]);

  const rowProps = useMemo(() => {
    return {
      onRequestVerification: handleRequestVerificationClick,
      onEditPort: handleEditPortClick,
      cancelSubLoading: cancelSubscriptionLoading,
      onCancelSubscription: handleCancelSubscription,
      onSaleClick: handlePutForSaleOpen,
      onSaleEdit: handleSaleEdit,
      onRemoveClick: handleRemoveClick,
      removeLoadingId,
    };
  }, [
    handleRequestVerificationClick,
    handleEditPortClick,
    cancelSubscriptionLoading,
    handleCancelSubscription,
    handlePutupForSaleClick,
    handleSaleEdit,
    handleRemoveClick,
    removeLoadingId,
  ]);

  useEffect(() => {
    updateCollapsibleContent();
  }, [
    fetchNetworkElements,
    setNetworkElements,
    removeLoadingId,
    expandedNeId,
    portsInNe,
    groupsInNe,
    portsGroupsLoading,
    selectedUnGroupId,
    order,
    orderBy,
    groupedSelected,
    unGroupedSelected,
    expandedGroupIds,
    handleCollapsibleNEClose,
    handleCollapsibleNEOpen,
    handleRequestSort,
    handleRowSelect,
    handleGroupRowSelect,
    handleCollapsibleGroupOpen,
    handleCollapsibleGroupClose,
    networkElements,
    expandedNeId,
    handleEditPortClick,
    handleCancelSubscription,
    handlePutForSaleOpen,
    handleSaleEdit,
    handleSaleEdit,
    cancelSubscriptionLoading,
  ]);

  const totalPages = Math.ceil(total / TableConstants.perPage);
  return (
    <>
      {isNodeView ? (
        <div className="verification-root manage-link-view" data-testid="admin-verification">
          <div className="sticky-top">
            <div className="title-main">
              <div className="title-root">
                <h3>Manage Ports</h3>
              </div>
            </div>
            <div className="title-sub">
              <div className="dropdown-filters">
                <Dropdown
                  title="Select Node"
                  className="bandwidth filter"
                  inputValue={neInput}
                  onInputChange={handleNeInputChange}
                  value={searchTextNeElements}
                  onNetNames={networkElements.filter((item: any) => item.on_net).map((item: any) => item.ne_name)}
                  options={networkElements.map((item: any) => item.ne_name)}
                  onChange={handleNeValueChange}
                />
              </div>
              <div>
                <div className="group-ungroup-div">
                  <Button
                    variant="grouped"
                    node="Add Port"
                    className="verify-all-btn"
                    onClick={() => navigate(AuthRoutes.addPort, { state: currentNetworkElement })}
                  />
                  <div className="navigation">
                    <img src={ListView} alt="List View" onClick={() => handlePortViewChange(false)} style={{ cursor: "pointer" }} />
                    <img src={NodeViewSelected} alt="Port View" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="sticky-bottom">{collapsibleContent}</div>
        </div>
      ) : (
        <div className="verification-root manage-link-view" data-testid="admin-verification">
          <div className="sticky-top">
            <div className="title-main">
              <div className="title-root">
                <h3>Manage Ports</h3>
              </div>
            </div>
            <div className="title-sub">
              <div className="flex">
                <div className="dropdown-filters">
                  <Dropdown
                    title="Select Node"
                    className="bandwidth filter"
                    inputValue={neInput}
                    onInputChange={handleNeInputChange}
                    value={searchTextNeElements}
                    onNetNames={networkElements.filter((item: any) => item.on_net).map((item: any) => item.ne_name)}
                    options={networkElements.map((item: any) => item.ne_name)}
                    onChange={handleNeValueChange}
                  />
                </div>
                <div className="search-box" style={{ width: "100%" }}>
                  <div className="group-ungroup-div table-filters">
                    <Input
                      variant="input-pre-icon"
                      name="user-search"
                      placeholder="Search"
                      icon={SearchIcon}
                      onChange={handleSearchChange}
                      onKeypress={handleKeypress}
                      onBlur={() => {
                        if (searchText !== prevSearchText) fetchPortsAndGroups();
                      }}
                    />
                    {/* <div className="search-button-div" style={{ paddingLeft: "3px" }}>
                      <Button variant="search" node="Search" className="search-btn" disabled={searchText === prevSearchText} onClick={() => fetchListPorts()} />
                    </div> */}
                  </div>
                </div>
              </div>
              <div className="group-ungroup-div table-filters">
                <Button
                  variant="grouped"
                  node="Add Port"
                  className="verify-all-btn"
                  onClick={() => navigate(AuthRoutes.addPort, { state: currentNetworkElement })}
                />
                <div className="navigation">
                  <img src={ListViewSelected} alt="List View" />
                  <img src={NodeView} style={{ cursor: "pointer" }} onClick={() => handlePortViewChange(true)} alt="Port View" />
                </div>
              </div>
            </div>
          </div>
          <div className="sticky-bottom">
            <ListPortsTable
              refetch={refetch}
              loading={listLoading}
              ports={listPorts}
              totalPages={totalPages}
              page={page}
              order={order}
              orderBy={orderBy}
              rowProps={rowProps}
              onPageChange={handlePageChange}
              onRequestSort={handleRequestSort}
            />
          </div>
        </div>
      )}
      {GroupDrawerProps.drawerComponent}
      {SaleDrawerComponent}
    </>
  );
};

export default ManagePorts;
